import { SectionCard } from '@/components/SectionCard'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Patient, PatientSearchResult } from '../../types/patient'
import { PatientDialog } from '../PatientDialog'
import { PatientSearch, PatientSearchRef } from './PatientSearch'

type Props = {
  onSelect: (patient: Patient | null) => void
  maxResults?: number
  testId?: string
}

export type PatientSearchSelectorRef = {
  reset: () => void
}

export const PatientSearchSelector = forwardRef<PatientSearchSelectorRef, Props>(
  ({ onSelect, maxResults = 5, testId }, ref) => {
    const { t } = useTranslation()
    const patientSearchRef = useRef<PatientSearchRef>(null)

    const [hasSearched, setHasSearched] = useState(false)
    const [patients, setPatients] = useState<Patient[]>([])
    const [hisPatient, setHisPatient] = useState<Patient | null>(null)
    const [patientOpen, setPatientOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useImperativeHandle(ref, () => ({
      reset() {
        setPatients([])
        setHasSearched(false)
        setIsLoading(false)

        patientSearchRef.current?.reset()
      },
    }))

    function handleResult(result: PatientSearchResult) {
      setHasSearched(true)
      setPatients([])

      if (result.source === 'his') {
        setHisPatient({
          id: '',
          hn: result.data.hn,
          bedId: '',
          doctorId: '',

          departmentId: '',
          departmentName: '',

          title: result.data.title ?? '',
          fullName: '',
          firstName: result.data.firstName ?? '',
          midName: '',
          lastName: result.data.lastName ?? '',

          sex: result.data.gender ?? '',
          birthDate: result.data.birthDate ?? '',

          bloodGroup: '',
          nationality: '',
          race: '',
          religion: '',
          job: '',

          cardId: result.data.nationalId ?? '',
          passport: '',

          phoneNumber: '',
          address: '',
          email: '',

          emergencyName: '',
          emergencyPhoneNumber: '',
          emergencyRelation: '',

          historyMedicine: '',
          note: '',
        })

        setPatientOpen(true)

        return
      }

      setPatients(result.data)
    }

    return (
      <>
        <PatientSearch
          ref={patientSearchRef}
          onResult={handleResult}
          onLoading={setIsLoading}
          testId={testId}
          onSearchStart={() => {
            setHasSearched(true)
            setPatients([])
            onSelect(null)
          }}
        />

        {hasSearched && (
          <div className="mt-2">
            <SectionCard title={t('common.patients')} headerClass="pb-0">
              {isLoading ? (
                <div className="py-4 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
              ) : patients.length === 0 ? (
                <div className="py-4 text-center text-sm text-muted-foreground">{t('patient.notFound')}</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {patients.slice(0, maxResults).map(patient => (
                    <div
                      key={patient.id}
                      data-testid={testId ? `${testId}-option-${patient.id}` : undefined}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setPatients([])
                        setHasSearched(false)

                        onSelect(patient)
                      }}
                      className="flex items-center justify-between rounded-lg border border-border/30 bg-secondary/20 p-3 cursor-pointer transition-colors hover:bg-secondary/40 hover:border-primary/30"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-medium text-foreground">{patient.fullName} </span>
                          <span className="text-[10px] text-muted-foreground">
                            HN {patient.hn || '-'} | Card ID {patient.cardId || '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        )}

        <PatientDialog
          open={patientOpen}
          onOpenChange={setPatientOpen}
          hisPatient={hisPatient}
          patientId={null}
          onSuccess={patient => {
            setPatients([])
            setHasSearched(false)
            onSelect(patient)
          }}
        />
      </>
    )
  },
)
